import { ClientHomeRouteService } from './../../../services/utils/client-home-route.service';
import { IRegistration } from './../../../models/registration/registration';
import { IRegistrationState } from './../../state/registration.state';
import { getRegistrationState } from './../selectors/registration.selectors';
import { TokenService } from './../../../services/token/token.service';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { switchMap, catchError, map, withLatestFrom, concatMap } from 'rxjs/operators';
import { RegisterService } from 'src/app/services/http/register.service';
import { of } from 'rxjs';
import * as ErrorActions from '../../global-notification/actions/global-notification.action';
import * as registrationActions from './../actions/registration.actions';
import * as userActions from './../../user/actions/user.actions';
import * as authActions from '../../auth/actions/auth.actions';
import { Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { selectRegistrants } from '../selectors/registrants.selectors';
import { selectBankDetails } from '../selectors/banks.selectors';
import { AppConfig } from 'src/app/app.config';
import { SaveFailedComponent } from 'src/app/components/features/registration/modals/save-failed/save-failed.component';
import { DialogService } from 'src/app/services/utils/dialog.service';
import { RegistrationStatus } from 'src/app/models/lookups/registrationStatus';
import { SnackBarService } from 'src/app/services/snackbar/snackBar.service';
import { IEnvironmentConfig } from 'src/app/models/environmentConfig';

@Injectable()
export class RegistrationEffects {

  constructor(
    private actions$: Actions,
    private appConfig: AppConfig,
    private router: Router,
    private store: Store<IRegistrationState>,
    private registerService: RegisterService,
    private tokenService: TokenService,
    private dialogService: DialogService,
    private clientHomeRouteService: ClientHomeRouteService,
    private snackBarService: SnackBarService
  ) {}

  register$ = createEffect(() => this.actions$.pipe(
    ofType(registrationActions.REGISTER),
    switchMap((action: registrationActions.Register) => {
      const newInvestor = action.payload.newInvestor;
      const envSettings: IEnvironmentConfig = this.appConfig.getEnvironmentConfig();
      return this.registerService.register(newInvestor).pipe(
        switchMap(() => {
          return of(
            new authActions.Authenticate(newInvestor.email, newInvestor.password),
            new registrationActions.RegisterSuccess()
          );
        }),
        catchError((err) => of(new registrationActions.RegisterFail(this.transformError(err))))
      );
    })
  ));

  registrationGetStatus$ = createEffect(() => this.actions$.pipe(
    ofType(registrationActions.REGISTER_GET_STATUS),
    switchMap(() => {
      const investorEntityId = this.tokenService.getFirstEntityRelationship()?.baseEntityId;
      if(!investorEntityId){
          return;
      }
      return this.registerService.getRegistrationStatus(investorEntityId).pipe(
        switchMap((status: number) => {
          return of(new registrationActions.RegistrationRedirect(status))
        }),
        catchError((err) => of(
          new ErrorActions.NotificationErrorAction("Registration status could not be retrieved."),
          new registrationActions.RegistrationGetStatusFail(err)),
        )
      );
    })
  ));

  registrationLoadSteps$ = createEffect(() => this.actions$.pipe(
    ofType<registrationActions.RegistrationLoadSteps>(registrationActions.REGISTER_LOAD_STEPS),
    switchMap((action: registrationActions.RegistrationLoadSteps) => {
      const investorEntityId = this.tokenService.getFirstEntityRelationship()?.baseEntityId;
      return this.registerService.getRegistrationStep(investorEntityId).pipe(
        map((res: IRegistration) => {
          return new registrationActions.RegistrationLoadStepsSuccess(res);
        }),
        catchError((err) => of(
            new ErrorActions.NotificationErrorAction(err),
            new registrationActions.RegistrationLoadStepsFail(err)
        ))
      );
    })
  ));

  registrationRedirect$ = createEffect(() => this.actions$.pipe(
    ofType<registrationActions.RegistrationRedirect>(registrationActions.REGISTER_REDIRECT),
    map((action) => {
      const status = action.payload.status;
      if ((status === 1) || (status > 6)) {
        // Opening or registration step
        // this.router.navigate((status === 1) || (status % 100 === 0) ? ['/registration', 'welcome'] : ['/registration', 'steps']);
        this.router.navigate(['/registration', 'welcome']);
      } else if (status === 3) {
        // Active
        this.clientHomeRouteService.redirectToHome();
      } else {
        console.warn('Registration status not handled: ' + status);
      }
    })
  ), { dispatch: false });

  registrationSuccessRedirect$ = createEffect(() => this.actions$.pipe(
    ofType<registrationActions.RegistrationSuccessRedirect>(registrationActions.REGISTER_SUCCESS_REDIRECT),
    map((action) => {
      const status = action.payload.status;
      if (status === 3) {
        // Active
        this.router.navigate(['/registration', 'complete']);
      }
    })
  ), { dispatch: false });

  registrationShowFailedModal$ = createEffect(() => this.actions$.pipe(
    ofType<registrationActions.RegistrationShowFailedModal>(registrationActions.REGISTER_SHOW_FAILED_MODAL),
    map((action) => {
      const status = action.payload.status;
      if (status === 2) {
        this.dialogService.open(SaveFailedComponent);
      }
    })
  ), { dispatch: false });

  registrationSave$ = createEffect(() => this.actions$.pipe(
    ofType<registrationActions.RegistrationSave>(registrationActions.REGISTER_SAVE),
    withLatestFrom(
      this.store.pipe(select(getRegistrationState)),
      this.store.pipe(select(selectRegistrants)),
      this.store.pipe(select(selectBankDetails))
    ),
    switchMap(([action, state, registrants, banks]) => {
      const updateValue = {
        status: state.status.status,
        investorDetails: state.investor.details,
        registrants: registrants,
        bankDetails: banks,
        siteId: Number.parseInt(this.appConfig.getEnvironment().siteId, 10),
      };

      return this.registerService.save(updateValue).pipe(
        concatMap((res: IRegistration) => {
          this.snackBarService.showSnackBar('Save successful!');
          return [
            new registrationActions.RegistrationSaveSuccess(res),
            new registrationActions.RegistrationSuccessRedirect(res.status)
          ];
        }),
        catchError(err => {
          this.snackBarService.showWarningSnackBar('Save failed!');
          return of(
            new registrationActions.RegistrationSaveFail(err),
            new registrationActions.RegistrationShowFailedModal(state.status.status)
          );
        }));
    })
  ));

  registrationFinalSave$ = createEffect(() => this.actions$.pipe(
    ofType<registrationActions.RegistrationSave>(registrationActions.REGISTER_FINAl_SAVE),
    withLatestFrom(
      this.store.pipe(select(getRegistrationState)),
      this.store.pipe(select(selectRegistrants)),
      this.store.pipe(select(selectBankDetails))
    ),
    switchMap(([action, state, registrants, banks]) => {
      const updateValue = {
        status: RegistrationStatus.DETAILS_COMPLETE,
        investorDetails: state.investor.details,
        registrants: registrants,
        bankDetails: banks,
        siteId: Number.parseInt(this.appConfig.getEnvironment().siteId, 10),
      };

      return this.registerService.save(updateValue).pipe(
        concatMap((res: IRegistration) => {
          this.snackBarService.showSnackBar('Save successful!');
          return [
            new registrationActions.RegistrationFinalSaveSuccess(res),
            new userActions.GetUserProfile(),
            new registrationActions.RegistrationSuccessRedirect(res.status)
          ];
        }),
        catchError(err => {
          this.snackBarService.showWarningSnackBar('Save failed!');
          return of(
            new registrationActions.RegistrationFinalSaveFail(err),
            new registrationActions.RegistrationShowFailedModal(state.status.status)
          );
        }));
    })
  ));

  transformError(err: any): string {
    const messages: string[] = [];

    if (err && err.error.modelState) {
      Object.values(err.error.modelState).map((e: string[]) => e.map(x => messages.push(x)));
    }
    return messages.join('<br/>');
  }
}
